import React, { useContext } from 'react';
import { MdMenuBook, MdOutlineSearch } from 'react-icons/md';
import { useLocation } from 'react-use';
import { ProductContainerContext } from '@rsc/contexts/ProductContainerContext';
import RichText from '@rsc/ui-components/dept/molecules/RichText';
import Link from '@rsc/ui-components/tailwind/Link';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

interface Props {
  manuals: { asset: { url: string }; label?: string }[];
  supportPage?: string;
  warnings?: {
    _key: string;
    warning: { block: any };
  }[];
  warranties?: {
    _key: string;
    warranty: { block: any };
  }[];
}

const SupportCard = ({
  manuals,
  supportPage = null,
  warnings = null,
  warranties = null,
}: Readonly<Props>) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { activeVariant } = useContext(ProductContainerContext) as any;
  const sku = searchParams.get('sku') || activeVariant?.sku || '';
  const { tt } = useUiComponentsInternationalContext();
  return (
    <div className="flex flex-col rounded-lg bg-none p-0 pb-4 gap-4 md:bg-muted md:p-4 md:pb-8">
      <h2>{tt(s => s.supportCardComponent.supportCardHeader)}</h2>
      <div className="flex flex-col gap-4 lg:flex-row lg:pb-2 lg:border-b lg:border-border">
        {manuals?.[0] && (
          <div className="flex flex-1 items-end gap-2 pb-2 border-b border-border lg:border-0 lg:pb-0">
            <MdMenuBook className="text-foreground-link h-6 w-6 md:text-muted-foreground-link" />
            <Link
              className="text-foreground-link font-bold text-sm hover:underline md:text-muted-foreground-link"
              link={manuals[0]?.asset?.url}
            >
              {manuals[0]?.label ||
                tt(s => s.supportCardComponent.productManualLink)}
            </Link>
          </div>
        )}
        <div className="flex flex-1 items-center gap-2 pb-2 border-b border-border lg:border-0 lg:pb-0">
          <MdOutlineSearch className="text-foreground-link h-6 w-6 md:text-muted-foreground-link" />
          <Link
            className="text-foreground-link font-bold text-sm hover:underline md:text-muted-foreground-link"
            link={supportPage || 'https://support.petsafe.com'}
          >
            {tt(s => s.supportCardComponent.moreSupportTopicsLink)}
          </Link>
        </div>
      </div>
      {sku && (
        <div className="flex flex-col gap-1">
          <h3 className="text-base">
            {tt(s => s.supportCardComponent.itemModelNumberLabel)}
          </h3>
          <span className="text-foreground">{sku}</span>
        </div>
      )}
      {warnings?.filter(w => w.warning?.block)?.length > 0 && (
        <div className="flex flex-col gap-1 [&>p]:my-0 [&>p]:text-foreground">
          <h3 className="text-base">
            {warnings?.length > 1
              ? tt(s => s.supportCardComponent.warningsLabel)
              : tt(s => s.supportCardComponent.warningLabel)}
          </h3>
          {warnings
            ?.filter(w => w.warning?.block)
            .map(w => (
              <RichText body={w.warning.block} key={w._key} />
            ))}
        </div>
      )}
      {warranties && (
        <div className="flex flex-col gap-1 [&>p]:my-0 [&>p]:text-foreground">
          <h3 className="text-base">
            {warranties?.length > 1
              ? tt(s => s.supportCardComponent.productWarrantiesLabel)
              : tt(s => s.supportCardComponent.productWarrantyLabel)}
          </h3>
          {warranties
            ?.filter(w => w.warranty?.block)
            .map(w => (
              <RichText body={w.warranty.block} key={w._key} />
            ))}
        </div>
      )}
    </div>
  );
};

export default SupportCard;
